import { oc } from "@orpc/contract";
import { z } from "zod";
import { arStatementSendTaskCustomerSchema } from "@erp/shared-types";

/**
 * Live re-resolution of a payment-reminder task's customer set.
 *
 * A `ar_bulk_statement_send` task carries the customer list its scan resolved,
 * which goes stale as soon as anyone sends a statement by hand — the task keeps
 * listing customers who no longer need one until the next scheduled scan. This
 * returns the set that still needs a reminder right now, resolved by the same
 * server-side eligibility rules the scan uses (aging query + cooldown + comms
 * opt-outs + positive balance).
 *
 * Takes the task id, not the criteria: the reminder terms live on the task's own
 * action payload (persisted there by the scan from its step config), so the
 * server reads them itself rather than trusting a caller to echo them back.
 */
export const arPaymentRemindersContracts = {
  resolveCustomers: oc
    .input(
      z.object({
        /** An `ar_bulk_statement_send` task in the caller's organization. */
        taskId: z.uuid(),
      }),
    )
    .output(
      z.object({
        customers: z.array(arStatementSendTaskCustomerSchema),
        resolvedAt: z.iso.datetime(),
      }),
    )
    .route({
      method: "GET",
      summary: "List customers still needing a payment reminder",
      description:
        "Re-checks a payment-reminder task and returns the customers who still need a statement now, applying aging, cooldown, communication opt-out and positive-balance rules. Read-only.",
    }),
} as const;
